<template>
  <div class="race-manager">
    <!-- Header -->
    <div class="race-header">
      <div class="header-info">
        <h2>
          <i class="fas fa-certificate"></i>
          RACE Approval Toolkit
        </h2>
        <p class="header-description">
          Prepare, validate, and track RACE (Registry of Approved Continuing Education) applications 
          for AAVSB veterinary CE credit approval.
        </p>
      </div>
      <a 
        href="https://www.aavsb.org/ce-services/race/review-the-race-standards" 
        target="_blank" 
        class="btn btn-outline btn-sm"
      >
        <i class="fas fa-external-link-alt"></i>
        AAVSB RACE Standards
      </a>
    </div>

    <!-- Quick Actions Bar -->
    <div class="quick-actions">
      <div class="action-card fee-calculator-card" @click="showFeeCalculator = true">
        <i class="fas fa-calculator"></i>
        <span>Fee Calculator</span>
      </div>
      <a 
        href="https://cebroker.com/providers" 
        target="_blank" 
        class="action-card"
      >
        <i class="fas fa-cloud-upload-alt"></i>
        <span>CE Broker Portal</span>
      </a>
      <a 
        href="https://www.aavsb.org/wp-content/uploads/2025/01/RACE-Program-Course-Attachment.pdf" 
        target="_blank" 
        class="action-card"
      >
        <i class="fas fa-file-pdf"></i>
        <span>Course Attachment Template</span>
      </a>
      <a 
        href="https://www.aavsb.org/wp-content/uploads/2025/01/RACE-Full-Program-Attendee-List-Template.xlsx" 
        target="_blank" 
        class="action-card"
      >
        <i class="fas fa-list-alt"></i>
        <span>Attendee List Template</span>
      </a>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading courses...</p>
    </div>

    <!-- Course List with RACE Status -->
    <div v-else class="courses-race-list">
      <div v-if="courses.length === 0" class="empty-state">
        <i class="fas fa-graduation-cap"></i>
        <h3>No courses found</h3>
        <p>Create courses in the Courses tab first, then come back to prepare RACE applications.</p>
      </div>

      <div 
        v-for="course in courses" 
        :key="course.id" 
        class="race-course-card"
        :class="{ 'expanded': expandedCourse === course.id }"
      >
        <div class="course-summary" @click="toggleCourse(course)">
          <div class="course-info">
            <h3>{{ course.title }}</h3>
            <div class="course-meta-badges">
              <span class="meta-badge" v-if="course.credit_hours">
                <i class="fas fa-clock"></i>
                {{ course.credit_hours }} CE Hours
              </span>
              <span class="meta-badge" v-if="course.video_count">
                <i class="fas fa-video"></i>
                {{ course.video_count }} Videos
              </span>
              <span class="meta-badge" v-if="course.quiz_question_count">
                <i class="fas fa-question-circle"></i>
                {{ course.quiz_question_count }} Quiz Questions
              </span>
              <span class="meta-badge" v-if="course.instructor_name">
                <i class="fas fa-user-md"></i>
                {{ course.instructor_name }}
              </span>
            </div>
          </div>

          <div class="race-status-section">
            <div 
              v-if="course.race_application" 
              class="race-status-badge"
              :class="getStatusClass(course.race_application.status)"
            >
              <i :class="getStatusIcon(course.race_application.status)"></i>
              {{ course.race_application.status_display }}
            </div>
            <div v-else class="race-status-badge status-none">
              <i class="fas fa-plus-circle"></i>
              Not Started
            </div>
            <!-- Phase 3c — re-submission needed banner -->
            <div
              v-if="course.race_application && course.race_application.requires_resubmission"
              class="race-status-badge status-resubmit"
              :title="resubmissionTooltip(course.race_application.resubmission_reason)"
            >
              <i class="fas fa-redo-alt"></i>
              Re-submission needed
            </div>

            <div v-if="course.race_application && course.race_application.readiness_score !== null" class="readiness-score">
              <div 
                class="score-circle"
                :class="getScoreClass(course.race_application.readiness_score)"
              >
                {{ course.race_application.readiness_score }}%
              </div>
            </div>

            <button class="btn-expand">
              <i :class="expandedCourse === course.id ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>
          </div>
        </div>

        <!-- Expanded Detail Panel -->
        <div v-if="expandedCourse === course.id" class="course-detail-panel">
          <!-- Action Buttons -->
          <div class="detail-actions">
            <button 
              @click="runReadinessCheck(course)" 
              :disabled="checkingReadiness === course.id"
              class="btn btn-primary"
            >
              <i :class="checkingReadiness === course.id ? 'fas fa-spinner fa-spin' : 'fas fa-clipboard-check'"></i>
              {{ checkingReadiness === course.id ? 'Checking...' : 'Run Readiness Check' }}
            </button>
            <button @click="viewCourseAttachment(course)" class="btn btn-secondary">
              <i class="fas fa-file-alt"></i>
              View Course Attachment
            </button>
            <button @click="downloadCourseAttachmentPdf(course)" class="btn btn-secondary">
              <i class="fas fa-file-pdf"></i>
              Download PDF
            </button>
            <button @click="viewAdvertisingLanguage(course)" class="btn btn-secondary">
              <i class="fas fa-bullhorn"></i>
              Advertising Language
            </button>
            <button @click="openApplicationEditor(course)" class="btn btn-outline">
              <i class="fas fa-edit"></i>
              Edit Application
            </button>
            <button
              v-if="course.race_application?.delivery_method === 'INTERACTIVE'"
              @click="openLiveAttendancePicker(course)"
              class="btn btn-outline"
            >
              <i class="fas fa-users"></i>
              Live Attendance CSV
            </button>
            <button
              @click="submitToCEBrokerPortal(course)"
              :disabled="portalSubmitting === course.id"
              class="btn btn-outline"
              title="Trigger the CE Broker portal bot to fill the new-application form using the latest PDF + structured data."
            >
              <i :class="portalSubmitting === course.id ? 'fas fa-spinner fa-spin' : 'fas fa-robot'"></i>
              {{ portalSubmitting === course.id ? 'Submitting…' : 'Submit via Portal Bot' }}
            </button>
          </div>

          <!-- Phase 3b — Live attendance event picker (loaded on demand) -->
          <div
            v-if="liveAttendanceEvents[course.id]"
            class="live-attendance-panel"
          >
            <h4>
              <i class="fas fa-users"></i>
              Live event attendance — pick an event to download the RACE-format CSV
            </h4>
            <p v-if="!liveAttendanceEvents[course.id].length" class="muted">
              No live events found for this course.
            </p>
            <ul v-else class="live-attendance-list">
              <li
                v-for="ev in liveAttendanceEvents[course.id]"
                :key="ev.id"
              >
                <div>
                  <strong>{{ ev.title }}</strong>
                  <span class="muted">— {{ formatChangeAt(ev.scheduled_start) }}</span>
                </div>
                <button class="btn btn-secondary btn-sm" @click="downloadLiveAttendanceCsv(ev)">
                  <i class="fas fa-download"></i>
                  Download CSV
                </button>
              </li>
            </ul>
          </div>

          <!-- Phase 3.5 — portal bot result panel -->
          <div
            v-if="portalSubmitResults[course.id]"
            class="portal-result-panel"
            :class="{ 'portal-error': portalSubmitResults[course.id].status === 'error' }"
          >
            <h4>
              <i :class="portalSubmitResults[course.id].status === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
              CE Broker portal submission — {{ portalSubmitResults[course.id].status }}
            </h4>
            <p>{{ portalSubmitResults[course.id].message }}</p>
            <ul v-if="portalSubmitResults[course.id].screenshots?.length" class="portal-screenshots">
              <li v-for="(s, i) in portalSubmitResults[course.id].screenshots" :key="i">
                <a :href="s.url" target="_blank" rel="noopener">{{ s.label || `screenshot-${i + 1}` }}</a>
              </li>
            </ul>
          </div>

          <!-- Phase 3c — Re-submission required panel -->
          <div
            v-if="course.race_application && course.race_application.requires_resubmission"
            class="resubmission-panel"
          >
            <div class="resubmission-header">
              <i class="fas fa-redo-alt"></i>
              <div>
                <h4>RACE Re-submission Required</h4>
                <p>
                  Course content has changed since AAVSB approved this RACE
                  application. Regenerate the package, upload the updated PDF
                  to CE Broker, then mark this application as re-submitted.
                </p>
              </div>
            </div>
            <ul class="resubmission-changes">
              <li
                v-for="(c, idx) in (course.race_application.resubmission_reason?.changes || []).slice(-15)"
                :key="'rc-' + idx"
              >
                <code v-if="c?.at">{{ formatChangeAt(c.at) }}</code>
                <span>{{ c?.change || c }}</span>
              </li>
            </ul>
            <div class="resubmission-actions">
              <button @click="downloadCourseAttachmentPdf(course)" class="btn btn-primary">
                <i class="fas fa-file-pdf"></i>
                Generate Re-submission Package
              </button>
              <button
                @click="clearResubmissionFlag(course)"
                :disabled="clearingResubmission === course.id"
                class="btn btn-outline"
              >
                <i :class="clearingResubmission === course.id ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                Mark as Re-submitted
              </button>
            </div>
          </div>

          <!-- Readiness Results -->
          <div v-if="readinessResults[course.id]" class="readiness-results">
            <div class="readiness-header">
              <div class="readiness-overview">
                <div 
                  class="readiness-score-large"
                  :class="getScoreClass(readinessResults[course.id].score)"
                >
                  <span class="score-number">{{ readinessResults[course.id].score }}%</span>
                  <span class="score-label">Ready</span>
                </div>
                <div class="readiness-summary-text">
                  <h4 v-if="readinessResults[course.id].overall_ready" class="text-success">
                    <i class="fas fa-check-circle"></i> Ready for RACE Submission
                  </h4>
                  <h4 v-else class="text-warning">
                    <i class="fas fa-exclamation-triangle"></i> Action Required
                  </h4>
                  <p>
                    {{ readinessResults[course.id].summary.passed }} of 
                    {{ readinessResults[course.id].summary.total_checks }} checks passed
                    ({{ readinessResults[course.id].summary.required_passed }} of 
                    {{ readinessResults[course.id].summary.required_total }} required)
                  </p>
                </div>
              </div>
            </div>

            <!-- Critical Issues -->
            <div v-if="readinessResults[course.id].critical_issues.length > 0" class="issues-section critical">
              <h5><i class="fas fa-times-circle"></i> Critical Issues (Must Fix)</h5>
              <ul>
                <li v-for="(issue, idx) in readinessResults[course.id].critical_issues" :key="'ci-'+idx">
                  {{ issue }}
                </li>
              </ul>
            </div>

            <!-- Recommendations -->
            <div v-if="readinessResults[course.id].recommendations.length > 0" class="issues-section recommendations">
              <h5><i class="fas fa-lightbulb"></i> Recommendations</h5>
              <ul>
                <li v-for="(rec, idx) in readinessResults[course.id].recommendations" :key="'rec-'+idx">
                  {{ rec }}
                </li>
              </ul>
            </div>

            <!-- Detailed Checklist -->
            <div class="checklist-section">
              <h5><i class="fas fa-tasks"></i> Detailed Checklist</h5>
              <div class="checklist-grid">
                <div 
                  v-for="check in readinessResults[course.id].checks" 
                  :key="check.id"
                  class="checklist-item"
                  :class="{ 'passed': check.passed, 'failed': !check.passed, 'optional': !check.required }"
                >
                  <div class="check-icon">
                    <i v-if="check.passed" class="fas fa-check-circle"></i>
                    <i v-else-if="check.required" class="fas fa-times-circle"></i>
                    <i v-else class="fas fa-info-circle"></i>
                  </div>
                  <div class="check-content">
                    <div class="check-label">
                      {{ check.label }}
                      <span v-if="!check.required" class="optional-tag">Optional</span>
                    </div>
                    <div class="check-detail">{{ check.detail }}</div>
                    <div class="check-values">
                      <span class="current">Current: {{ check.current_value }}</span>
                      <span class="required">Required: {{ check.requirement }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Fee Estimate -->
          <div v-if="course.race_application && course.race_application.readiness_score !== null" class="fee-estimate-inline">
            <h5><i class="fas fa-dollar-sign"></i> Estimated RACE Fees</h5>
            <div class="fee-quick-display">
              <span class="fee-amount">${{ calculateQuickFee(course) }}</span>
              <span class="fee-note">Based on {{ course.credit_hours || 0 }} CE hours (new application)</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fee Calculator Modal -->
    <div v-if="showFeeCalculator" class="modal-overlay" @click.self="showFeeCalculator = false">
      <div class="modal-content modal-sm" @click.stop>
        <div class="modal-header">
          <h2><i class="fas fa-calculator"></i> RACE Fee Calculator</h2>
          <button @click="showFeeCalculator = false" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>CE Credit Hours</label>
            <input 
              v-model.number="feeCalc.ceHours" 
              type="number" 
              step="0.25" 
              min="0.25" 
              class="form-input"
              placeholder="e.g., 2.5"
            />
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input v-model="feeCalc.isRenewal" type="checkbox" />
              <span>Renewal Application</span>
            </label>
          </div>
          <div class="form-group">
            <label>Projected Offering Date</label>
            <input 
              v-model="feeCalc.offeringDate" 
              type="date" 
              class="form-input"
            />
            <small>Late fee applies if within 45 days of offering</small>
          </div>

          <button @click="calculateFees" :disabled="calculatingFees" class="btn btn-primary btn-block">
            <i :class="calculatingFees ? 'fas fa-spinner fa-spin' : 'fas fa-calculator'"></i>
            {{ calculatingFees ? 'Calculating...' : 'Calculate Fees' }}
          </button>

          <div v-if="feeResults" class="fee-results">
            <div class="fee-breakdown">
              <div 
                v-for="(fee, key) in feeResults.fees" 
                :key="key"
                class="fee-line"
                :class="{ 'fee-total': key === 'total', 'fee-late': key === 'late_fee' && fee.applies }"
              >
                <span class="fee-label">{{ fee.label }}</span>
                <span class="fee-value">
                  <template v-if="key === 'late_fee' && !fee.applies">
                    N/A
                  </template>
                  <template v-else>
                    ${{ fee.amount.toFixed(2) }}
                  </template>
                </span>
              </div>
            </div>
            <div v-if="feeResults.is_late" class="late-warning">
              <i class="fas fa-exclamation-triangle"></i>
              Late fee applies - application is within 45 days of projected offering date.
            </div>
            <p class="fee-note-text">{{ feeResults.note }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Course Attachment Modal -->
    <div v-if="showAttachmentModal" class="modal-overlay" @click.self="showAttachmentModal = false">
      <div class="modal-content modal-lg" @click.stop>
        <div class="modal-header">
          <h2><i class="fas fa-file-alt"></i> RACE Course Attachment</h2>
          <button @click="showAttachmentModal = false" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body" v-if="attachmentData">
          <div class="attachment-document">
            <div class="attachment-section">
              <h3>Program Title</h3>
              <p>{{ attachmentData.program_title }}</p>
            </div>

            <div class="attachment-section">
              <h3>Program Description</h3>
              <p class="description-text">{{ attachmentData.program_description }}</p>
              <div v-if="attachmentData.program_keywords" class="keywords">
                <strong>Keywords:</strong> {{ attachmentData.program_keywords }}
              </div>
            </div>

            <div class="attachment-section">
              <h3>Learning Objectives</h3>
              <ol>
                <li v-for="(obj, idx) in attachmentData.learning_objectives" :key="idx">{{ obj }}</li>
              </ol>
              <p v-if="!attachmentData.learning_objectives.length" class="missing-data">
                <i class="fas fa-exclamation-triangle"></i> No learning objectives set
              </p>
            </div>

            <div class="attachment-section">
              <h3>Program Agenda</h3>
              <table class="agenda-table" v-if="attachmentData.program_agenda.length">
                <thead>
                  <tr>
                    <th>Session</th>
                    <th>Length</th>
                    <th>Presenter</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(session, idx) in attachmentData.program_agenda" :key="idx">
                    <td>{{ session.session_name }}</td>
                    <td>{{ session.length_display }}</td>
                    <td>{{ session.presenter }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Total</strong></td>
                    <td><strong>{{ attachmentData.total_duration_minutes }} min</strong></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
              <p v-else class="missing-data">
                <i class="fas fa-exclamation-triangle"></i> No sessions/videos added
              </p>
            </div>

            <div class="attachment-section">
              <h3>CE Credit Request</h3>
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">Total Credits:</span>
                  <span class="detail-value">{{ attachmentData.ce_credit_request.total_credits }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Category:</span>
                  <span class="detail-value">{{ attachmentData.ce_credit_request.category }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Delivery Method:</span>
                  <span class="detail-value">{{ attachmentData.ce_credit_request.delivery_method }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Calculation:</span>
                  <span class="detail-value">{{ attachmentData.ce_credit_request.calculation_basis }}</span>
                </div>
              </div>
            </div>

            <div class="attachment-section">
              <h3>Presenter Information</h3>
              <div v-for="(presenter, idx) in attachmentData.presenter_information" :key="idx" class="presenter-card">
                <h4>{{ presenter.name }}</h4>
                <p v-if="presenter.credentials"><strong>Credentials:</strong> {{ presenter.credentials }}</p>
                <p v-if="presenter.bio" class="presenter-bio">{{ presenter.bio }}</p>
              </div>
              <p v-if="!attachmentData.presenter_information.length" class="missing-data">
                <i class="fas fa-exclamation-triangle"></i> No presenter information set
              </p>
            </div>

            <div class="attachment-section">
              <h3>Assessment</h3>
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">Post-Test:</span>
                  <span class="detail-value">{{ attachmentData.assessment.has_post_test ? 'Yes' : 'No' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Questions:</span>
                  <span class="detail-value">{{ attachmentData.assessment.number_of_questions }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Passing Score:</span>
                  <span class="detail-value">{{ attachmentData.assessment.passing_score }}%</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Questions per Credit:</span>
                  <span class="detail-value">{{ attachmentData.assessment.questions_per_credit }}</span>
                </div>
              </div>
            </div>
          </div>

          <button @click="copyAttachmentToClipboard" class="btn btn-primary btn-block" style="margin-top: 1rem;">
            <i class="fas fa-copy"></i>
            Copy to Clipboard
          </button>
        </div>
      </div>
    </div>

    <!-- Advertising Language Modal -->
    <div v-if="showAdLanguageModal" class="modal-overlay" @click.self="showAdLanguageModal = false">
      <div class="modal-content modal-sm" @click.stop>
        <div class="modal-header">
          <h2><i class="fas fa-bullhorn"></i> RACE Advertising Language</h2>
          <button @click="showAdLanguageModal = false" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body" v-if="adLanguageData">
          <div :class="['ad-status-badge', adLanguageData.status === 'approved' ? 'approved' : 'pending']">
            <i :class="adLanguageData.status === 'approved' ? 'fas fa-check-circle' : 'fas fa-hourglass-half'"></i>
            {{ adLanguageData.status === 'approved' ? 'Approved Program' : 'Pending Approval' }}
          </div>
          <div class="ad-language-text">
            <p>{{ adLanguageData.language }}</p>
          </div>
          <div v-if="adLanguageData.note" class="ad-language-note">
            <i class="fas fa-info-circle"></i>
            {{ adLanguageData.note }}
          </div>
          <button @click="copyToClipboard(adLanguageData.language)" class="btn btn-primary btn-block" style="margin-top: 1rem;">
            <i class="fas fa-copy"></i>
            Copy Language
          </button>
        </div>
      </div>
    </div>

    <!-- Application Editor Modal -->
    <div v-if="showApplicationEditor" class="modal-overlay" @click.self="showApplicationEditor = false">
      <div class="modal-content modal-lg" @click.stop>
        <div class="modal-header">
          <h2><i class="fas fa-edit"></i> RACE Application Details</h2>
          <button @click="showApplicationEditor = false" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-section">
            <h3>Application Status</h3>
            <div class="form-row">
              <div class="form-group">
                <label>Status</label>
                <select v-model="appForm.status" class="form-select">
                  <option value="NOT_STARTED">Not Started</option>
                  <option value="PREPARING">Preparing Application</option>
                  <option value="READY_TO_SUBMIT">Ready to Submit</option>
                  <option value="SUBMITTED">Submitted to RACE</option>
                  <option value="UNDER_REVIEW">Under Committee Review</option>
                  <option value="APPROVED">RACE Approved</option>
                  <option value="DENIED">Denied</option>
                  <option value="REVISION_REQUESTED">Revision Requested</option>
                  <option value="EXPIRED">Approval Expired</option>
                </select>
              </div>
              <div class="form-group">
                <label>RACE Provider Number</label>
                <input v-model="appForm.race_provider_number" type="text" class="form-input" placeholder="From CE Broker" />
              </div>
            </div>
          </div>

          <div class="form-section">
            <h3>Program Details</h3>
            <div class="form-row">
              <div class="form-group">
                <label>Program Category</label>
                <select v-model="appForm.program_category" class="form-select">
                  <option value="MEDICAL">Medical</option>
                  <option value="NON_MEDICAL">Non-Medical (Practice Mgmt/Prof Dev/Wellness)</option>
                </select>
              </div>
              <div class="form-group">
                <label>Delivery Method</label>
                <select v-model="appForm.delivery_method" class="form-select">
                  <option value="INTERACTIVE">Interactive (Live/Webinar/Workshop)</option>
                  <option value="NON_INTERACTIVE">Non-Interactive (On-Demand/Recorded)</option>
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>CE Credits Requested</label>
                <input v-model.number="appForm.ce_credits_requested" type="number" step="0.25" min="0.25" class="form-input" />
              </div>
              <div class="form-group">
                <label>Projected Offering Date</label>
                <input v-model="appForm.projected_offering_date" type="date" class="form-input" />
              </div>
            </div>
          </div>

          <div class="form-section">
            <h3>Approval Details</h3>
            <div class="form-row">
              <div class="form-group">
                <label>RACE Program Number</label>
                <input v-model="appForm.race_program_number" type="text" class="form-input" placeholder="Assigned after approval" />
              </div>
              <div class="form-group">
                <label>Approval Date</label>
                <input v-model="appForm.approval_date" type="date" class="form-input" />
              </div>
            </div>
          </div>

          <div class="form-section">
            <h3>Compliance</h3>
            <div class="form-group">
              <label class="checkbox-label">
                <input v-model="appForm.has_commercial_support" type="checkbox" />
                <span>Program has commercial/corporate support</span>
              </label>
            </div>
            <div class="form-group">
              <label>Conflict of Interest Disclosure</label>
              <textarea 
                v-model="appForm.conflict_of_interest_disclosure" 
                class="form-textarea" 
                rows="3"
                placeholder="Disclose any relationships between provider, presenter, and content..."
              ></textarea>
            </div>
          </div>

          <div class="form-section">
            <h3>Notes</h3>
            <div class="form-group">
              <label>Internal Notes</label>
              <textarea v-model="appForm.notes" class="form-textarea" rows="3" placeholder="Internal tracking notes..."></textarea>
            </div>
            <div class="form-group">
              <label>RACE Committee Feedback</label>
              <textarea 
                v-model="appForm.race_committee_feedback" 
                class="form-textarea" 
                rows="3" 
                placeholder="Paste any feedback from RACE committee..."
              ></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showApplicationEditor = false" class="btn btn-secondary">Cancel</button>
          <button @click="saveApplication" :disabled="savingApplication" class="btn btn-primary">
            <i :class="savingApplication ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
            {{ savingApplication ? 'Saving...' : 'Save Application' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { lmsAPI } from '@/services/lmsAPI.js'
import api from '@/services/api'
import { useToast } from 'vue-toastification'

export default {
  name: 'RACEApprovalManager',
  setup() {
    const toast = useToast()

    const loading = ref(true)
    const courses = ref([])
    const expandedCourse = ref(null)
    const checkingReadiness = ref(null)
    const readinessResults = reactive({})

    // Fee calculator
    const showFeeCalculator = ref(false)
    const calculatingFees = ref(false)
    const feeCalc = reactive({
      ceHours: 1,
      isRenewal: false,
      offeringDate: '',
    })
    const feeResults = ref(null)

    // Course attachment
    const showAttachmentModal = ref(false)
    const attachmentData = ref(null)

    // Advertising language
    const showAdLanguageModal = ref(false)
    const adLanguageData = ref(null)

    // Application editor
    const showApplicationEditor = ref(false)
    const savingApplication = ref(false)
    const editingCourseId = ref(null)
    const appForm = reactive({
      status: 'NOT_STARTED',
      program_category: 'MEDICAL',
      delivery_method: 'NON_INTERACTIVE',
      race_provider_number: '',
      race_program_number: '',
      ce_credits_requested: null,
      projected_offering_date: '',
      approval_date: '',
      has_commercial_support: false,
      conflict_of_interest_disclosure: '',
      notes: '',
      race_committee_feedback: '',
    })

    const loadCourses = async () => {
      loading.value = true
      try {
        const response = await lmsAPI.getRACECourses()
        courses.value = response.data.courses || []
      } catch (err) {
        console.error('Error loading RACE courses:', err)
        toast.error('Failed to load courses for RACE review')
      } finally {
        loading.value = false
      }
    }

    const toggleCourse = (course) => {
      expandedCourse.value = expandedCourse.value === course.id ? null : course.id
    }

    const runReadinessCheck = async (course) => {
      checkingReadiness.value = course.id
      try {
        const response = await lmsAPI.checkRACEReadiness(course.id)
        readinessResults[course.id] = response.data.readiness

        // Reload to get updated status
        await loadCourses()

        if (response.data.readiness.overall_ready) {
          toast.success('Course is ready for RACE submission!')
        } else {
          toast.warning(`${response.data.readiness.critical_issues.length} issue(s) need attention`)
        }
      } catch (err) {
        console.error('Error checking readiness:', err)
        toast.error('Failed to run readiness check')
      } finally {
        checkingReadiness.value = null
      }
    }

    const calculateFees = async () => {
      calculatingFees.value = true
      try {
        const response = await lmsAPI.calculateRACEFees({
          ce_hours: feeCalc.ceHours,
          is_renewal: feeCalc.isRenewal,
          offering_date: feeCalc.offeringDate || null,
        })
        feeResults.value = response.data
      } catch (err) {
        console.error('Error calculating fees:', err)
        toast.error('Failed to calculate fees')
      } finally {
        calculatingFees.value = false
      }
    }

    const calculateQuickFee = (course) => {
      const hours = course.credit_hours || 0
      let adminFee = 190
      let hourlyFee = 0

      if (hours >= 1 && hours <= 24) {
        hourlyFee = Math.ceil(hours) * 25
      } else if (hours >= 25 && hours <= 49) {
        hourlyFee = 600
      } else if (hours >= 50 && hours <= 99) {
        hourlyFee = 800
      } else if (hours >= 100 && hours <= 249) {
        hourlyFee = 1300
      } else if (hours >= 250) {
        hourlyFee = 2800
      }

      return (adminFee + hourlyFee).toFixed(2)
    }

    const viewCourseAttachment = async (course) => {
      try {
        const response = await lmsAPI.getRACECourseAttachment(course.id)
        attachmentData.value = response.data.attachment
        showAttachmentModal.value = true
      } catch (err) {
        console.error('Error loading course attachment:', err)
        toast.error('Failed to load course attachment data')
      }
    }

    // Phase 3a — download the RACE Course Attachment PDF directly.
    const downloadCourseAttachmentPdf = async (course) => {
      try {
        const response = await api.get(
          `/lms/race/course-attachment/${course.id}/?format=pdf`,
          { responseType: 'blob' },
        )
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `race-attachment-${course.slug || 'course-' + course.id}.pdf`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        toast.success('Course attachment PDF downloaded')
      } catch (err) {
        console.error('Error downloading PDF:', err)
        toast.error('Failed to download PDF')
      }
    }

    // Phase 3c — clear the re-submission flag once the admin uploaded the
    // updated package to CE Broker.
    const clearingResubmission = ref(null)
    const clearResubmissionFlag = async (course) => {
      if (!confirm('Mark this RACE application as re-submitted to AAVSB?')) return
      clearingResubmission.value = course.id
      try {
        await api.post(`/lms/race/clear-resubmission/${course.id}/`)
        toast.success('Marked as re-submitted')
        await loadCourses()
      } catch (err) {
        console.error('Failed to clear resubmission flag', err)
        toast.error('Failed to mark as re-submitted')
      } finally {
        clearingResubmission.value = null
      }
    }

    const resubmissionTooltip = (reason) => {
      if (!reason || !Array.isArray(reason.changes)) return ''
      const lastFew = reason.changes.slice(-3).map((c) => c?.change || c).join('\n')
      return `Recent changes:\n${lastFew}`
    }
    const formatChangeAt = (iso) => {
      try {
        return new Date(iso).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
      } catch (_) { return iso }
    }

    // Phase 3b — list live events for a course so the admin can download
    // the RACE-format attendance CSV from a chosen event.
    const liveAttendanceEvents = ref({})
    const openLiveAttendancePicker = async (course) => {
      try {
        const res = await api.get('/lms/live-events/', { params: { course: course.id } })
        const items = res.data?.results || res.data || []
        liveAttendanceEvents.value = {
          ...liveAttendanceEvents.value,
          [course.id]: items,
        }
      } catch (err) {
        console.error('Failed to load live events', err)
        toast.error('Could not load live events for this course.')
      }
    }
    const downloadLiveAttendanceCsv = async (ev) => {
      try {
        const res = await api.get(
          `/lms/race/live-attendance/${ev.id}/`,
          { responseType: 'blob' },
        )
        const blob = new Blob([res.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `race-live-attendance-event-${ev.id}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        toast.success('RACE live attendance CSV downloaded')
      } catch (err) {
        console.error('Failed to download live attendance CSV', err)
        toast.error('Failed to download attendance CSV')
      }
    }

    // Phase 3.5 — trigger the (optional) CE Broker portal bot.
    const portalSubmitting = ref(null)
    const portalSubmitResults = ref({})
    const submitToCEBrokerPortal = async (course) => {
      if (!confirm(
        'This will launch the CE Broker portal bot to fill the submission form for "'
        + course.title + '". Continue?'
      )) return
      portalSubmitting.value = course.id
      try {
        const res = await api.post(`/lms/race/portal-submit/${course.id}/`)
        portalSubmitResults.value = {
          ...portalSubmitResults.value,
          [course.id]: {
            status: res.data?.status || 'success',
            message: res.data?.message || 'Portal bot run completed.',
            screenshots: res.data?.screenshots || [],
          },
        }
        toast.success('Portal bot finished — review screenshots before final submit.')
      } catch (err) {
        const detail = err?.response?.data?.detail || err.message || 'Portal bot failed'
        portalSubmitResults.value = {
          ...portalSubmitResults.value,
          [course.id]: { status: 'error', message: detail, screenshots: [] },
        }
        console.error('Portal bot failed', err)
        toast.error('Portal bot failed — see admin panel for details.')
      } finally {
        portalSubmitting.value = null
      }
    }

    const viewAdvertisingLanguage = async (course) => {
      try {
        const response = await lmsAPI.getRACEAdvertisingLanguage(course.id)
        adLanguageData.value = response.data.advertising
        showAdLanguageModal.value = true
      } catch (err) {
        console.error('Error loading advertising language:', err)
        toast.error('Failed to load advertising language')
      }
    }

    const openApplicationEditor = (course) => {
      editingCourseId.value = course.id
      const app = course.race_application
      if (app) {
        Object.assign(appForm, {
          status: app.status || 'NOT_STARTED',
          program_category: app.program_category || 'MEDICAL',
          delivery_method: app.delivery_method || 'NON_INTERACTIVE',
          race_provider_number: app.race_provider_number || '',
          race_program_number: app.race_program_number || '',
          ce_credits_requested: app.ce_credits_requested || course.credit_hours,
          projected_offering_date: app.projected_offering_date || '',
          approval_date: app.approval_date || '',
          has_commercial_support: app.has_commercial_support || false,
          conflict_of_interest_disclosure: app.conflict_of_interest_disclosure || '',
          notes: app.notes || '',
          race_committee_feedback: app.race_committee_feedback || '',
        })
      } else {
        Object.assign(appForm, {
          status: 'NOT_STARTED',
          program_category: 'MEDICAL',
          delivery_method: course.course_type === 'ON_DEMAND' ? 'NON_INTERACTIVE' : 'INTERACTIVE',
          race_provider_number: '',
          race_program_number: '',
          ce_credits_requested: course.credit_hours,
          projected_offering_date: '',
          approval_date: '',
          has_commercial_support: false,
          conflict_of_interest_disclosure: '',
          notes: '',
          race_committee_feedback: '',
        })
      }
      showApplicationEditor.value = true
    }

    const saveApplication = async () => {
      savingApplication.value = true
      try {
        await lmsAPI.updateRACEApplication(editingCourseId.value, { ...appForm })
        toast.success('RACE application saved')
        showApplicationEditor.value = false
        await loadCourses()
      } catch (err) {
        console.error('Error saving application:', err)
        toast.error('Failed to save application')
      } finally {
        savingApplication.value = false
      }
    }

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text)
        toast.success('Copied to clipboard!')
      } catch {
        toast.error('Failed to copy')
      }
    }

    const copyAttachmentToClipboard = () => {
      if (!attachmentData.value) return
      const d = attachmentData.value
      let text = `RACE COURSE ATTACHMENT\n${'='.repeat(50)}\n\n`
      text += `PROGRAM TITLE: ${d.program_title}\n\n`
      text += `PROGRAM DESCRIPTION:\n${d.program_description}\n\n`
      if (d.program_keywords) text += `KEYWORDS: ${d.program_keywords}\n\n`
      text += `LEARNING OBJECTIVES:\n`
      d.learning_objectives.forEach((obj, i) => { text += `${i + 1}. ${obj}\n` })
      text += `\nPROGRAM AGENDA:\n`
      d.program_agenda.forEach(s => { text += `- ${s.session_name} (${s.length_display}) - ${s.presenter}\n` })
      text += `\nTotal Duration: ${d.total_duration_minutes} minutes\n\n`
      text += `CE CREDIT REQUEST:\n`
      text += `- Total Credits: ${d.ce_credit_request.total_credits}\n`
      text += `- Category: ${d.ce_credit_request.category}\n`
      text += `- Delivery: ${d.ce_credit_request.delivery_method}\n`
      text += `- Calculation: ${d.ce_credit_request.calculation_basis}\n\n`
      text += `PRESENTER INFORMATION:\n`
      d.presenter_information.forEach(p => {
        text += `- ${p.name} (${p.credentials})\n`
      })
      text += `\nASSESSMENT:\n`
      text += `- Post-Test: ${d.assessment.has_post_test ? 'Yes' : 'No'}\n`
      text += `- Questions: ${d.assessment.number_of_questions}\n`
      text += `- Passing Score: ${d.assessment.passing_score}%\n`
      text += `- Questions per Credit: ${d.assessment.questions_per_credit}\n\n`
      text += `TARGET AUDIENCE: ${d.target_audience}\n`
      text += `Generated: ${d.generated_date}\n`
      copyToClipboard(text)
    }

    const getStatusClass = (status) => {
      const map = {
        'NOT_STARTED': 'status-none',
        'PREPARING': 'status-preparing',
        'READY_TO_SUBMIT': 'status-ready',
        'SUBMITTED': 'status-submitted',
        'UNDER_REVIEW': 'status-review',
        'APPROVED': 'status-approved',
        'DENIED': 'status-denied',
        'REVISION_REQUESTED': 'status-revision',
        'EXPIRED': 'status-expired',
      }
      return map[status] || 'status-none'
    }

    const getStatusIcon = (status) => {
      const map = {
        'NOT_STARTED': 'fas fa-circle',
        'PREPARING': 'fas fa-wrench',
        'READY_TO_SUBMIT': 'fas fa-check-circle',
        'SUBMITTED': 'fas fa-paper-plane',
        'UNDER_REVIEW': 'fas fa-search',
        'APPROVED': 'fas fa-award',
        'DENIED': 'fas fa-times-circle',
        'REVISION_REQUESTED': 'fas fa-redo',
        'EXPIRED': 'fas fa-calendar-times',
      }
      return map[status] || 'fas fa-circle'
    }

    const getScoreClass = (score) => {
      if (score >= 90) return 'score-excellent'
      if (score >= 70) return 'score-good'
      if (score >= 50) return 'score-fair'
      return 'score-poor'
    }

    onMounted(() => {
      loadCourses()
    })

    return {
      loading,
      courses,
      expandedCourse,
      checkingReadiness,
      readinessResults,
      showFeeCalculator,
      calculatingFees,
      feeCalc,
      feeResults,
      showAttachmentModal,
      attachmentData,
      showAdLanguageModal,
      adLanguageData,
      showApplicationEditor,
      savingApplication,
      appForm,
      toggleCourse,
      runReadinessCheck,
      calculateFees,
      calculateQuickFee,
      viewCourseAttachment,
      downloadCourseAttachmentPdf,
      clearResubmissionFlag,
      clearingResubmission,
      resubmissionTooltip,
      formatChangeAt,
      liveAttendanceEvents,
      openLiveAttendancePicker,
      downloadLiveAttendanceCsv,
      portalSubmitting,
      portalSubmitResults,
      submitToCEBrokerPortal,
      viewAdvertisingLanguage,
      openApplicationEditor,
      saveApplication,
      copyToClipboard,
      copyAttachmentToClipboard,
      getStatusClass,
      getStatusIcon,
      getScoreClass,
    }
  }
}
</script>

<style scoped>
.race-manager {
  width: 100%;
}

/* Phase 3c — re-submission flag styling */
.status-resubmit {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #f59e0b;
}
.resubmission-panel {
  background: #fffbeb;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
}
.live-attendance-panel {
  background: #f0f9ff;
  border: 1px solid #38bdf8;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
}
.live-attendance-panel h4 { margin: 0 0 8px 0; color: #0369a1; font-size: 14px; }
.live-attendance-panel .muted { color: #6b7280; font-size: 13px; }
.live-attendance-list { list-style: none; padding: 0; margin: 8px 0 0 0; }
.live-attendance-list li {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 0; border-top: 1px solid #e0f2fe; font-size: 13px;
}
.live-attendance-list .muted { color: #6b7280; margin-left: 6px; }
.portal-result-panel {
  background: #ecfdf5;
  border: 1px solid #10b981;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  color: #065f46;
}
.portal-result-panel h4 { margin: 0 0 6px 0; font-size: 14px; }
.portal-result-panel.portal-error {
  background: #fef2f2;
  border-color: #ef4444;
  color: #991b1b;
}
.portal-screenshots { margin: 8px 0 0 16px; padding: 0; }
.portal-screenshots li a { color: inherit; text-decoration: underline; font-size: 13px; }
.btn-sm { padding: 4px 10px; font-size: 12px; }
.resubmission-header {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.resubmission-header i {
  color: #d97706;
  font-size: 22px;
  margin-top: 4px;
}
.resubmission-header h4 {
  margin: 0;
  color: #92400e;
}
.resubmission-header p {
  margin: 4px 0 0 0;
  color: #78350f;
  font-size: 14px;
}
.resubmission-changes {
  margin: 12px 0 12px 32px;
  padding-left: 0;
  list-style: disc;
  font-size: 13px;
  color: #78350f;
}
.resubmission-changes li { margin: 4px 0; }
.resubmission-changes code {
  background: #fde68a;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  margin-right: 6px;
}
.resubmission-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.race-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-xl);
}

.race-header h2 {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 1.5rem;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.race-header h2 i {
  color: #059669;
}

.header-description {
  color: var(--color-text-secondary);
  margin: 0;
  max-width: 600px;
  line-height: 1.5;
}

/* Quick Actions */
.quick-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-xl);
}

.action-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-lg);
  background: white;
  border: 2px solid var(--color-border);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  color: var(--color-text-primary);
  text-align: center;
}

.action-card:hover {
  border-color: #059669;
  background: #f0fdf4;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.1);
}

.action-card i {
  font-size: 1.75rem;
  color: #059669;
}

.action-card span {
  font-weight: 600;
  font-size: 0.9rem;
}

.fee-calculator-card {
  border-color: #7c3aed;
}

.fee-calculator-card:hover {
  border-color: #7c3aed;
  background: #f5f3ff;
}

.fee-calculator-card i {
  color: #7c3aed;
}

/* Course Cards */
.courses-race-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.race-course-card {
  background: white;
  border: 2px solid var(--color-border);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.race-course-card.expanded {
  border-color: #059669;
  box-shadow: 0 4px 16px rgba(5, 150, 105, 0.1);
}

.course-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-lg);
  cursor: pointer;
  transition: background 0.15s ease;
}

.course-summary:hover {
  background: #f9fafb;
}

.course-info h3 {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 1.15rem;
  color: var(--color-text-primary);
}

.course-meta-badges {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
}

.meta-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 0.8rem;
  color: var(--color-text-secondary);
}

.meta-badge i {
  font-size: 0.75rem;
  color: #6b7280;
}

.race-status-section {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-shrink: 0;
}

.race-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-none { background: #f3f4f6; color: #6b7280; }
.status-preparing { background: #fef3c7; color: #92400e; }
.status-ready { background: #d1fae5; color: #065f46; }
.status-submitted { background: #dbeafe; color: #1e40af; }
.status-review { background: #e0e7ff; color: #3730a3; }
.status-approved { background: #d1fae5; color: #065f46; border: 2px solid #10b981; }
.status-denied { background: #fee2e2; color: #991b1b; }
.status-revision { background: #fef3c7; color: #92400e; }
.status-expired { background: #f3f4f6; color: #6b7280; }

.readiness-score {
  flex-shrink: 0;
}

.score-circle {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;
}

.score-excellent { background: #d1fae5; color: #065f46; }
.score-good { background: #fef3c7; color: #92400e; }
.score-fair { background: #fed7aa; color: #9a3412; }
.score-poor { background: #fee2e2; color: #991b1b; }

.btn-expand {
  background: none;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: var(--spacing-sm);
  border-radius: 6px;
  transition: all 0.2s ease;
}

.btn-expand:hover {
  background: #f3f4f6;
  color: var(--color-text-primary);
}

/* Detail Panel */
.course-detail-panel {
  padding: 0 var(--spacing-lg) var(--spacing-lg);
  border-top: 1px solid var(--color-border);
}

.detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  padding: var(--spacing-lg) 0;
}

/* Readiness Results */
.readiness-results {
  background: #f9fafb;
  border-radius: 12px;
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
}

.readiness-header {
  margin-bottom: var(--spacing-lg);
}

.readiness-overview {
  display: flex;
  align-items: center;
  gap: var(--spacing-xl);
}

.readiness-score-large {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.readiness-score-large .score-number {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1;
}

.readiness-score-large .score-label {
  font-size: 0.7rem;
  font-weight: 500;
  opacity: 0.8;
}

.readiness-summary-text h4 {
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.text-success { color: #059669; }
.text-warning { color: #d97706; }

.readiness-summary-text p {
  color: var(--color-text-secondary);
  margin: 0;
}

/* Issues sections */
.issues-section {
  padding: var(--spacing-md);
  border-radius: 8px;
  margin-bottom: var(--spacing-md);
}

.issues-section.critical {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.issues-section.recommendations {
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.issues-section h5 {
  margin: 0 0 var(--spacing-sm) 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.issues-section.critical h5 { color: #dc2626; }
.issues-section.recommendations h5 { color: #d97706; }

.issues-section ul {
  margin: 0;
  padding-left: var(--spacing-lg);
}

.issues-section li {
  margin-bottom: var(--spacing-xs);
  line-height: 1.5;
}

/* Checklist */
.checklist-section {
  margin-top: var(--spacing-lg);
}

.checklist-section h5 {
  margin: 0 0 var(--spacing-md) 0;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.checklist-grid {
  display: grid;
  gap: var(--spacing-sm);
}

.checklist-item {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: white;
}

.checklist-item.passed {
  border-left: 4px solid #10b981;
}

.checklist-item.failed {
  border-left: 4px solid #ef4444;
}

.checklist-item.optional.failed {
  border-left: 4px solid #f59e0b;
}

.check-icon {
  flex-shrink: 0;
  font-size: 1.2rem;
}

.checklist-item.passed .check-icon { color: #10b981; }
.checklist-item.failed .check-icon { color: #ef4444; }
.checklist-item.optional .check-icon { color: #f59e0b; }

.check-label {
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.optional-tag {
  font-size: 0.7rem;
  background: #fef3c7;
  color: #92400e;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 500;
}

.check-detail {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
}

.check-values {
  display: flex;
  gap: var(--spacing-lg);
  font-size: 0.8rem;
}

.check-values .current {
  color: var(--color-text-secondary);
}

.check-values .required {
  color: #6b7280;
  font-style: italic;
}

/* Fee Estimate Inline */
.fee-estimate-inline {
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  border-radius: 8px;
  padding: var(--spacing-md) var(--spacing-lg);
}

.fee-estimate-inline h5 {
  margin: 0 0 var(--spacing-sm) 0;
  color: #7c3aed;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.fee-quick-display {
  display: flex;
  align-items: baseline;
  gap: var(--spacing-md);
}

.fee-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: #7c3aed;
}

.fee-note {
  color: var(--color-text-secondary);
  font-size: 0.85rem;
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-height: 90vh;
  overflow-y: auto;
  width: 100%;
}

.modal-sm { max-width: 550px; }
.modal-lg { max-width: 800px; }

.modal-header {
  padding: var(--spacing-lg) var(--spacing-xl);
  border-bottom: 2px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
  border-radius: 16px 16px 0 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.3rem;
  cursor: pointer;
  color: var(--color-text-secondary);
  padding: var(--spacing-sm);
  border-radius: 6px;
}

.modal-close:hover {
  background: #f3f4f6;
  color: var(--color-text-primary);
}

.modal-body {
  padding: var(--spacing-xl);
}

.modal-footer {
  padding: var(--spacing-lg) var(--spacing-xl);
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
}

/* Fee Results */
.fee-results {
  margin-top: var(--spacing-lg);
  background: #f9fafb;
  border-radius: 8px;
  padding: var(--spacing-lg);
}

.fee-breakdown {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.fee-line {
  display: flex;
  justify-content: space-between;
  padding: var(--spacing-sm) 0;
}

.fee-line.fee-total {
  border-top: 2px solid var(--color-border);
  padding-top: var(--spacing-md);
  margin-top: var(--spacing-sm);
  font-weight: 700;
  font-size: 1.1rem;
}

.fee-line.fee-late {
  color: #dc2626;
  font-weight: 600;
}

.late-warning {
  margin-top: var(--spacing-md);
  padding: var(--spacing-md);
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.fee-note-text {
  margin-top: var(--spacing-md);
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  font-style: italic;
}

/* Attachment Document */
.attachment-document {
  background: white;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: var(--spacing-xl);
}

.attachment-section {
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-lg);
  border-bottom: 1px solid var(--color-border);
}

.attachment-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.attachment-section h3 {
  color: #059669;
  font-size: 1.1rem;
  margin: 0 0 var(--spacing-md) 0;
}

.description-text {
  white-space: pre-line;
  line-height: 1.6;
}

.keywords {
  margin-top: var(--spacing-sm);
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.agenda-table {
  width: 100%;
  border-collapse: collapse;
}

.agenda-table th,
.agenda-table td {
  padding: var(--spacing-sm) var(--spacing-md);
  text-align: left;
  border-bottom: 1px solid var(--color-border);
}

.agenda-table th {
  background: #f9fafb;
  font-weight: 600;
  font-size: 0.9rem;
}

.agenda-table tfoot td {
  font-weight: 600;
  background: #f0fdf4;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  font-weight: 500;
}

.detail-value {
  font-weight: 600;
  color: var(--color-text-primary);
}

.presenter-card {
  background: #f9fafb;
  border-radius: 8px;
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
}

.presenter-card h4 {
  margin: 0 0 var(--spacing-xs) 0;
  color: var(--color-text-primary);
}

.presenter-bio {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
  white-space: pre-line;
}

.missing-data {
  color: #d97706;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

/* Advertising Language */
.ad-status-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: 8px;
  font-weight: 600;
  margin-bottom: var(--spacing-lg);
}

.ad-status-badge.approved {
  background: #d1fae5;
  color: #065f46;
}

.ad-status-badge.pending {
  background: #fef3c7;
  color: #92400e;
}

.ad-language-text {
  background: #f9fafb;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: var(--spacing-lg);
  line-height: 1.6;
}

.ad-language-note {
  margin-top: var(--spacing-md);
  padding: var(--spacing-md);
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 6px;
  color: #9a3412;
  font-size: 0.9rem;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
}

/* Form Styles */
.form-section {
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-xl);
  border-bottom: 1px solid var(--color-border);
}

.form-section:last-child {
  border-bottom: none;
}

.form-section h3 {
  margin: 0 0 var(--spacing-lg) 0;
  font-size: 1.1rem;
  color: var(--color-text-primary);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
}

.form-group {
  margin-bottom: var(--spacing-md);
}

.form-group label {
  display: block;
  margin-bottom: var(--spacing-xs);
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 0.9rem;
}

.form-group small {
  display: block;
  margin-top: 4px;
  color: var(--color-text-secondary);
  font-size: 0.8rem;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  font-weight: normal;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.btn-primary {
  background: #059669;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #047857;
}

.btn-secondary {
  background: white;
  color: #059669;
  border: 2px solid #059669;
}

.btn-secondary:hover {
  background: #f0fdf4;
}

.btn-outline {
  background: white;
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
}

.btn-outline:hover {
  border-color: #059669;
  color: #059669;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 0.85rem;
}

.btn-block {
  width: 100%;
  justify-content: center;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading & Empty States */
.loading-container {
  text-align: center;
  padding: var(--spacing-xxl);
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #059669;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto var(--spacing-md);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: var(--spacing-xxl);
  background: #f9fafb;
  border-radius: 12px;
}

.empty-state i {
  font-size: 3rem;
  color: var(--color-text-secondary);
  opacity: 0.5;
  margin-bottom: var(--spacing-md);
}

.empty-state h3 {
  margin: 0 0 var(--spacing-sm) 0;
}

.empty-state p {
  color: var(--color-text-secondary);
}

/* Responsive */
@media (max-width: 768px) {
  .race-header {
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .quick-actions {
    grid-template-columns: repeat(2, 1fr);
  }

  .course-summary {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-md);
  }

  .race-status-section {
    width: 100%;
    justify-content: space-between;
  }

  .detail-actions {
    flex-direction: column;
  }

  .detail-actions .btn {
    width: 100%;
    justify-content: center;
  }

  .readiness-overview {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .modal-content {
    max-width: 95% !important;
    margin: var(--spacing-md);
  }
}
</style>
